import type { DialogEdge, DialogNode, ScenarioContent } from '../../api/types';

export const NODE_TYPES = ['say', 'listen', 'branch', 'command', 'end'] as const;

export function emptyContent(): ScenarioContent {
  return {
    nodes: [
      { id: 'start', type: 'say', text: '' },
      { id: 'end', type: 'end' },
    ],
    edges: [{ from: 'start', to: 'end' }],
  };
}

export function normalizeContent(value: unknown): ScenarioContent {
  const v = (value ?? {}) as Partial<ScenarioContent>;
  return {
    ...v,
    nodes: Array.isArray(v.nodes) ? (v.nodes as DialogNode[]) : [],
    edges: Array.isArray(v.edges) ? (v.edges as DialogEdge[]) : [],
  };
}

export type GraphIssueCode =
  | 'noNodes'
  | 'missingId'
  | 'duplicateId'
  | 'edgeFromUnknown'
  | 'edgeToUnknown'
  | 'startUnknown'
  | 'sayWithoutText'
  | 'commandWithoutCommand'
  | 'endHasOutgoing'
  | 'noOutgoing'
  | 'endUnreachable';

export interface GraphIssue {
  code: GraphIssueCode;
  node?: string;
}

const STRUCTURAL: GraphIssueCode[] = ['missingId', 'duplicateId', 'edgeFromUnknown', 'edgeToUnknown'];

/**
 * Полная проверка графа — те же правила, что у scenario-service
 * (services/scenario-service/app/graph.py), чтобы ошибки были видны до сохранения.
 */
export function validateGraph(content: ScenarioContent): GraphIssue[] {
  const issues: GraphIssue[] = [];
  const ids = new Set<string>();
  for (const n of content.nodes) {
    if (!n.id) issues.push({ code: 'missingId' });
    else if (ids.has(n.id)) issues.push({ code: 'duplicateId', node: n.id });
    ids.add(n.id);
  }
  for (const e of content.edges) {
    if (!ids.has(e.from)) issues.push({ code: 'edgeFromUnknown', node: e.from });
    if (!ids.has(e.to)) issues.push({ code: 'edgeToUnknown', node: e.to });
  }
  if (!content.nodes.length) {
    issues.push({ code: 'noNodes' });
    return issues;
  }
  const start = typeof content.start === 'string' && content.start ? content.start : content.nodes[0].id;
  if (!ids.has(start)) issues.push({ code: 'startUnknown', node: start });
  const outgoing = new Map<string, string[]>();
  for (const e of content.edges) {
    if (ids.has(e.from) && ids.has(e.to)) outgoing.set(e.from, [...(outgoing.get(e.from) ?? []), e.to]);
  }
  for (const n of content.nodes) {
    if (!n.id) continue;
    const out = outgoing.get(n.id) ?? [];
    if (n.type === 'say' && (n.text === undefined || n.text === null)) issues.push({ code: 'sayWithoutText', node: n.id });
    if (n.type === 'command' && !n.command && !String(n.text ?? '').trim())
      issues.push({ code: 'commandWithoutCommand', node: n.id });
    if (n.type === 'end' && out.length) issues.push({ code: 'endHasOutgoing', node: n.id });
    if (n.type !== 'end' && !out.length) issues.push({ code: 'noOutgoing', node: n.id });
  }
  if (ids.has(start)) {
    const types = new Map(content.nodes.map((n) => [n.id, n.type]));
    const seen = new Set([start]);
    const queue = [start];
    while (queue.length) {
      for (const next of outgoing.get(queue.shift() as string) ?? []) {
        if (!seen.has(next)) {
          seen.add(next);
          queue.push(next);
        }
      }
    }
    if (![...seen].some((id) => types.get(id) === 'end')) issues.push({ code: 'endUnreachable' });
  }
  return issues;
}

const LEGACY_MESSAGES: Partial<Record<GraphIssueCode, (node?: string) => string>> = {
  missingId: () => 'node without id',
  duplicateId: (n) => `duplicate node id: ${n}`,
  edgeFromUnknown: (n) => `edge from unknown node: ${n}`,
  edgeToUnknown: (n) => `edge to unknown node: ${n}`,
};

/** Структурные ошибки (id и ссылки рёбер) в виде строк — для логов и совместимости. */
export function validateContent(content: ScenarioContent): string[] {
  return validateGraph(content)
    .filter((i) => STRUCTURAL.includes(i.code))
    .map((i) => (LEGACY_MESSAGES[i.code] as (node?: string) => string)(i.node));
}

export interface Point {
  x: number;
  y: number;
}

export function nodePosition(node: DialogNode): Point | null {
  const p = node.position as Partial<Point> | undefined;
  return p && typeof p.x === 'number' && typeof p.y === 'number' ? { x: p.x, y: p.y } : null;
}

/** Позиции узлов: сохранённые в node.position, остальные — автоматическая раскладка. */
export function positionsOf(content: ScenarioContent): Map<string, Point> {
  const auto = layoutGraph(content);
  const result = new Map<string, Point>();
  auto.forEach(({ node, x, y }) => result.set(node.id, nodePosition(node) ?? { x, y }));
  return result;
}

/** Переименование узла вместе со ссылками на него в рёбрах и в start. */
export function renameNode(content: ScenarioContent, from: string, to: string): ScenarioContent {
  return {
    ...content,
    start: content.start === from ? to : content.start,
    nodes: content.nodes.map((n) => (n.id === from ? { ...n, id: to } : n)),
    edges: content.edges.map((e) => ({ ...e, from: e.from === from ? to : e.from, to: e.to === from ? to : e.to })),
  };
}

export function removeNode(content: ScenarioContent, id: string): ScenarioContent {
  return {
    ...content,
    start: content.start === id ? undefined : content.start,
    nodes: content.nodes.filter((n) => n.id !== id),
    edges: content.edges.filter((e) => e.from !== id && e.to !== id),
  };
}

export function uniqueNodeId(content: ScenarioContent, base: string): string {
  const ids = new Set(content.nodes.map((n) => n.id));
  for (let i = 1; ; i++) {
    const id = `${base}${i}`;
    if (!ids.has(id)) return id;
  }
}

export function addEdge(content: ScenarioContent, from: string, to: string): ScenarioContent {
  if (content.edges.some((e) => e.from === from && e.to === to)) return content;
  return { ...content, edges: [...content.edges, { from, to }] };
}

export interface LayoutNode {
  node: DialogNode;
  x: number;
  y: number;
}

export function layoutGraph(content: ScenarioContent, colWidth = 170, rowHeight = 70): LayoutNode[] {
  const incoming = new Map<string, number>();
  for (const n of content.nodes) incoming.set(n.id, 0);
  for (const e of content.edges) incoming.set(e.to, (incoming.get(e.to) ?? 0) + 1);
  const depth = new Map<string, number>();
  const roots = content.nodes.filter((n) => (incoming.get(n.id) ?? 0) === 0).map((n) => n.id);
  const queue = roots.length ? [...roots] : content.nodes.slice(0, 1).map((n) => n.id);
  queue.forEach((id) => depth.set(id, 0));
  while (queue.length) {
    const id = queue.shift() as string;
    for (const e of content.edges.filter((edge) => edge.from === id)) {
      if (!depth.has(e.to)) {
        depth.set(e.to, (depth.get(id) ?? 0) + 1);
        queue.push(e.to);
      }
    }
  }
  const rows = new Map<number, number>();
  return content.nodes.map((node) => {
    const d = depth.get(node.id) ?? 0;
    const row = rows.get(d) ?? 0;
    rows.set(d, row + 1);
    return { node, x: 20 + d * colWidth, y: 20 + row * rowHeight };
  });
}
